import { format, isAfter, startOfMonth } from 'date-fns';
import React, { useEffect } from 'react';
import { DateUtils } from '../../core/utils/dateUtils';
import { FormInput, formInput } from './FormInput';
import { StyledTextInput, StyledTextInputProps } from './TextInput';

interface Props extends FormInput<Date | undefined>, StyledTextInputProps {
    dateTime?: boolean;
    monthOnly?: boolean;
    preventFutureMonths?: boolean;
    onClick?: () => void;
}

export const DateInput = formInput(function (props: Props) {
    useEffect(() => {
        if (props.monthOnly && !props.value) {
            // Set current date as initial value
            const currentDate = new Date();
            // Set the day to 1 to ensure we're just dealing with month/year
            currentDate.setDate(1);
            props.onChange?.(undefined, currentDate);
        }
    }, []); // Empty dependency array means this runs once when component mounts

    const onChange = (event: React.FormEvent<HTMLInputElement>) => {
        const newDate = DateUtils.toDate(event.currentTarget.value);

        if (props.preventFutureMonths && props.monthOnly && newDate) {
            const currentDate = startOfMonth(new Date());
            if (isAfter(newDate, currentDate)) {
                // If the selected date is in the future, don't update the value
                return;
            }
        }

        props.onChange?.(event, newDate);
    };

    const getDisplayValue = () => {
        if (!(props.value instanceof Date) || isNaN(props.value.getTime())) {
            return '';
        }

        if (props.monthOnly) {
            // For the HTML input, we need yyyy-MM format
            const htmlValue = format(props.value, 'yyyy-MM');
            return htmlValue;
        }

        return format(
            props.value,
            props.dateTime ? "yyyy-MM-dd'T'HH:mm" : 'yyyy-MM-dd'
        );
    };

    const getMaxDate = () => {
        if (props.preventFutureMonths && props.monthOnly) {
            return format(new Date(), 'yyyy-MM');
        }
        return undefined;
    };

    return (
    <StyledTextInput
      type={ props.monthOnly ? 'month' : props.dateTime ? 'datetime-local' : 'date'}
      ref={props.inputRef}
      name={props.name}
      placeholder={props.monthOnly ? 'mm/yyyy' : props.placeholder}
      value={getDisplayValue()}
      max={getMaxDate()}
      onClick={props.onClick}
      onChange={onChange}
      disabled={props.disabled}
      fullWidth={props.fullWidth}
    />
  );
});
